import React, { useState, ReactNode, useEffect, useRef } from "react";
import { IShape } from "../entites";
import { Path } from "../path/path";
import { Maybe } from "../../../core/models";
import classes from "./shape_viewer.module.scss";
import classNames from "classnames";
import { invertShape, IPosition, calcShapeCoordinates } from "./utils";
import { IImage } from "../../../core/image";
import { ExtendedLink } from "../../../i18n";

const SVG_ID = "SHAPE_VIEWER_ID";

interface IProps {
  image: Maybe<IImage>;
  shapes: Maybe<IShape[]>;
  svgStyles?: React.CSSProperties;
  invertHover?: boolean;
  hoverColor: string;
  renderShapeLink: (shape: IShape) => string;
  renderShapeOnClickLink?: (shape: IShape) => void;
  renderShapeOverlayText?: (shape: IShape) => Maybe<string[]>;
  renderHovered?: (shape: IShape) => Maybe<ReactNode>;
  hoveredSide?: "left" | "right";
  onHoveredChanged?: (shape: Maybe<IShape>) => void;
  preserveAspectRatio?: string;
  hoveredShape?: IShape;
}

interface IState {
  overlayShape: IShape;
  overlayShapeHovered: boolean;
  hoveredShape: Maybe<IShape>;
  hoverShapePosition: Maybe<IPosition>;
}

const initialState: IState = {
  overlayShape: {
    id: "overlayShape",
    closed: true,
    points: [
      {
        x: 0,
        y: 0,
      },
    ],
  },
  overlayShapeHovered: false,
  hoveredShape: undefined,
  hoverShapePosition: undefined,
};

interface ISvgTransform {
  scaleX: number;
  scaleY: number;
  originX: number;
  originY: number;
}

function computeSvgTransform(
  svg: SVGSVGElement | null,
  container: HTMLElement | null,
  imgW: number,
  imgH: number,
  preserve: string
): ISvgTransform | undefined {
  if (!svg || !container || !imgW || !imgH) return undefined;
  const svgRect = svg.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const cssW = svgRect.width;
  const cssH = svgRect.height;
  if (cssW <= 0 || cssH <= 0) return undefined;
  const svgLeft = svgRect.left - containerRect.left;
  const svgTop = svgRect.top - containerRect.top;
  if (preserve === "none") {
    return {
      scaleX: cssW / imgW,
      scaleY: cssH / imgH,
      originX: svgLeft,
      originY: svgTop,
    };
  }
  const scale = Math.min(cssW / imgW, cssH / imgH);
  const contentW = imgW * scale;
  const contentH = imgH * scale;
  return {
    scaleX: scale,
    scaleY: scale,
    originX: svgLeft + (cssW - contentW) / 2,
    originY: svgTop + (cssH - contentH) / 2,
  };
}

function shapeCenter(shape: IShape): { x: number; y: number; w: number; h: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of shape.points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2, w: maxX - minX, h: maxY - minY };
}

export function ShapeViewer({
  image,
  shapes,
  svgStyles,
  invertHover,
  hoverColor,
  renderShapeLink,
  renderShapeOnClickLink,
  renderHovered,
  hoveredSide,
  onHoveredChanged,
  preserveAspectRatio,
  hoveredShape,
  renderShapeOverlayText,
}: IProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const preserve = preserveAspectRatio ?? "none";

  const [state, setState] = useState<IState>(initialState);
  const [svgTransform, setSvgTransform] = useState<ISvgTransform | undefined>(undefined);

  useEffect(() => {
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container || !image?.width || !image?.height) return;
    const update = () => {
      setSvgTransform(computeSvgTransform(svg, container, image.width, image.height, preserve));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(svg);
    ro.observe(container);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [image?.width, image?.height, preserve]);

  function setHoveredShape(shape: IShape, position: Maybe<IPosition>) {
    if (image) {
      const invertedShape = invertShape(shape, image?.width, image?.height);
      onHoveredChanged && onHoveredChanged(shape);

      setState({
        overlayShape: invertHover ? invertedShape : shape,
        overlayShapeHovered: true,
        hoveredShape: shape,
        hoverShapePosition: position,
      });
    }
  }

  function getPosition(focusedShape: IShape): IPosition | undefined {
    const svgPosition = svgRef.current?.getBoundingClientRect();
    const shapePosition = document.getElementById(focusedShape.id)?.getBoundingClientRect();

    const position = calcShapeCoordinates(svgPosition, shapePosition, hoveredSide);
    return position;
  }

  useEffect(() => {
    if (hoveredShape) {
      setHoveredShape(hoveredShape, getPosition(hoveredShape));
    } else {
      setState(initialState);
    }
  }, [hoveredShape]);

  function onMouseEnter(shape: IShape) {
    setHoveredShape(shape, getPosition(shape));
  }

  function onMouseLeave() {
    setState(initialState);
    onHoveredChanged && onHoveredChanged(undefined);
  }

  return (
    <div ref={containerRef} style={{ position: "relative", height: "100%", width: "100%" }}>
      <svg
        viewBox={`0 0 ${image?.width} ${image?.height}`}
        style={svgStyles}
        className={classes.svg}
        preserveAspectRatio={preserveAspectRatio ?? "none"}
        version="1.1"
        ref={svgRef}
        id={SVG_ID}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <image
          xlinkHref={image?.url}
          className={classes.image}
          x="0"
          y="0"
          width={image?.width}
          height={image?.height}
        ></image>

        <Path
          shape={state.overlayShape}
          fill={hoverColor}
          strokeWidth={0}
          className={classNames(classes.overlay, {
            [classes.overlayActive]: true,
          })}
        />

        {shapes?.map((shape) => {
          const text = renderShapeOverlayText?.(shape);

          if (text) {
            return (
              <Path
                key={shape.id}
                shape={shape}
                fill="rgba(235, 247, 250, 0.4)"
                strokeWidth={0}
                className={classNames("editor-status-overlay", classes.overlay, {
                  [classes.overlayActive]: true,
                })}
              />
            );
          }

          return (
            <ExtendedLink
              key={shape.id}
              to={renderShapeLink(shape)}
              onClick={renderShapeOnClickLink
                ? () => { renderShapeOnClickLink(shape) }
                : undefined
              }
            >
              <Path
                id={shape.id}
                shape={shape}
                fill="transparent"
                strokeWidth={0}
                onMouseEnter={() => onMouseEnter(shape)}
                onMouseLeave={onMouseLeave}
              />
            </ExtendedLink>
          );
        })}
      </svg>
      {svgTransform && shapes?.map((shape) => {
        const text = renderShapeOverlayText?.(shape);
        if (!text || text.length === 0) return null;
        const c = shapeCenter(shape);
        const cx = c.x * svgTransform.scaleX + svgTransform.originX;
        const cy = c.y * svgTransform.scaleY + svgTransform.originY;
        const shapeCssW = c.w * svgTransform.scaleX;
        const shapeCssH = c.h * svgTransform.scaleY;
        const longest = text.reduce((m, t) => Math.max(m, t.length), 0);
        const byWidth = (shapeCssW * 0.95) / (longest * 0.6);
        const byHeight = shapeCssH / (text.length * 1.2 + 0.3);
        const fitPx = Math.min(byWidth, byHeight);
        const fontSize = Math.max(12, Math.min(36, fitPx || 12));
        return (
          <div
            key={`label-${shape.id}`}
            className="editor-status-label"
            style={{
              position: "absolute",
              left: cx,
              top: cy,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              color: "rgb(182, 11, 11)",
              fontWeight: 800,
              fontFamily: "SF-Pro, Arial, sans-serif",
              fontSize,
              lineHeight: 1.1,
              textAlign: "center",
              textShadow:
                "0 0 3px rgba(255,255,255,0.95), 0 0 3px rgba(255,255,255,0.95), 0 0 3px rgba(255,255,255,0.95), 0 1px 2px rgba(255,255,255,0.9)",
              whiteSpace: "nowrap",
              zIndex: 2,
              userSelect: "none",
            }}
          >
            {text.map((t, i) => (
              <div key={`${t}-${i}`}>{t}</div>
            ))}
          </div>
        );
      })}
      {renderHovered && state.hoveredShape && state.hoverShapePosition && (
        <div
          style={{
            position: "absolute",
            top: state.hoverShapePosition.top,
            left: state.hoverShapePosition.left,
            right: state.hoverShapePosition.right,
            bottom: state.hoverShapePosition.bottom,
            zIndex: 3
          }}
        >
          {renderHovered(state.hoveredShape)}
        </div>
      )}
    </div>
  );
}
